import { isImmutable } from 'immutable';

import { axiosInstance } from '@/api/axiosInstance';
import type { PaginatedResponse } from '@/types';

import { updateApplication } from './api';
import type { ApplicationData } from './Application';

const applicationBase = '/applications/v1/application/';
const llmModelsBase = '/applications/v1/llm-models/';

export type MessagingPartnerData = {
  uuid: string;
  name: string;
  slug: string;
  hidden: false;
  active: boolean;
};

export type HiddenPartnerData = {
  uuid: string;
  hidden: true;
};

export type MessageSenderData = MessagingPartnerData | HiddenPartnerData;

export type LangfuseProjectData = {
  project_id: string;
  validated_at: string;
};

export type ApplicationLlmData = {
  month_usd: number;
  app_monthly_usd: number;
  viewer_monthly_usd: number;
  warning_percent: number;
  paused: boolean;
  langfuse: LangfuseProjectData | null;
};

export type ApplicationStorageCounts = {
  value_bytes: number;
  value_count: number;
  file_bytes: number;
  file_count: number;
};

export type ApplicationStorageLimits = {
  app: ApplicationStorageCounts;
  user: ApplicationStorageCounts;
  value_max_bytes: number;
  file_max_bytes: number;
};

export type ApplicationStorageData = {
  shared: ApplicationStorageCounts;
  viewers_with_data: number;
  limits: ApplicationStorageLimits;
};

export type LangfuseProjectWrite = {
  project_id: string;
  public_key: string;
  secret_key: string;
};

export type LlmModelData = {
  id: string;
  name: string;
  provider: string;
  endpoints: string[];
  max_output_tokens: number;
  supports: {
    tools: boolean;
    images: boolean;
    temperature: boolean;
    system: boolean;
  };
  price_per_mtok_usd: {
    input: number;
    output: number;
  };
};

export type LlmModelAliases = Record<'fast' | 'smart', string | null>;

export type LlmModelList = {
  aliases: LlmModelAliases;
  models: LlmModelData[];
};

export const toPlain = <T>(value: unknown): T | null => {
  if (value === null || value === undefined) return null;
  return (isImmutable(value) ? value.toJS() : value) as T;
};

export const fetchLlmModels = async ({
  signal,
}: { signal?: AbortSignal } = {}): Promise<LlmModelList> => {
  const response = await axiosInstance.get<LlmModelList>(llmModelsBase, {
    signal,
  });
  return response.data;
};

export const setLangfuseProject = (
  uuid: string,
  project: LangfuseProjectWrite | null,
) => updateApplication(uuid, { langfuse_project: project });

export const searchMessagingCandidates = async (
  search: string,
  { signal, pageSize = 20 }: { signal?: AbortSignal; pageSize?: number } = {},
) => {
  const response = await axiosInstance.get<PaginatedResponse<ApplicationData>>(
    applicationBase,
    {
      params: {
        messaging_candidate: 'true',
        page_size: String(pageSize),
        ...(search ? { search } : {}),
      },
      signal,
    },
  );
  return response.data.results;
};
